import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Users, 
  Settings, 
  Mail, 
  Trash2, 
  Crown,
  UserPlus,
  Shield,
  Copy,
  AlertCircle,
  Camera,
  Upload
} from 'lucide-react';
import { useTeam } from '../contexts/TeamContext';
import { useAuth } from '../../auth/contexts/AuthContext';
import { supabase } from '../../../shared/lib/supabase';
import { TopBar } from '../../../shared/components/navigation/TopBar';
import type { TeamWithMembers } from '../lib/teams';

interface TeamMember {
  id: string;
  user_id: string;
  role: 'owner' | 'member';
  joined_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  avatar_url?: string | null;
}

interface TeamInvitation {
  id: string;
  invited_email: string;
  role: 'member';
  status: 'pending' | 'sent' | 'accepted' | 'rejected' | 'expired';
  invited_by: string;
  expires_at: string;
  created_at: string;
}

interface TeamSettings {
  name: string;
  avatar_url: string | null;
}

export function TeamSettingsPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { refreshTeams } = useTeam();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(true);
  const [isInviting, setIsInviting] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamInvitations, setTeamInvitations] = useState<TeamInvitation[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<TeamWithMembers | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [settings, setSettings] = useState<TeamSettings>({
    name: '',
    avatar_url: null,
  });

  useEffect(() => {
    if (teamId) {
      loadTeam();
    }
  }, [teamId]);

  useEffect(() => {
    if (currentTeam) {
      loadTeamData();
      setSettings({
        name: currentTeam.name,
        avatar_url: currentTeam.avatar_url || null,
      });
    }
  }, [currentTeam]);

  const loadTeam = async () => {
    if (!teamId || !user) return;
    
    setIsLoading(true);
    try {
      // Load team details
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('*')
        .eq('id', teamId)
        .single();

      if (teamError) {
        console.error('Error loading team:', teamError);
        throw teamError;
      }

      // Check if user is a member
      const { data: membership, error: memberError } = await supabase
        .from('team_members')
        .select('role')
        .eq('team_id', teamId)
        .eq('user_id', user.id)
        .single();

      if (memberError && memberError.code !== 'PGRST116') {
        console.error('Error checking membership:', memberError);
        throw memberError;
      }

      if (!membership) {
        // User is not a member of this team
        toast.error('You are not a member of this team');
        navigate('/');
        return;
      }

      // Set team member status immediately
      setIsTeamMember(true);
      
      // Create team object with ownership info
      const teamWithOwnership: TeamWithMembers = {
        ...team,
        is_owner: membership.role === 'owner',
        team_members: []
      };

      setCurrentTeam(teamWithOwnership);
    } catch (error) {
      console.error('Error loading team:', error);
      toast.error('Failed to load team');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeamData = async () => {
    if (!currentTeam || !user) return;
    
    try {
      console.log('🔍 Loading team data for team:', currentTeam.id);
      console.log('🔍 Current user ID:', user.id);

      // Load team members
      const { data: basicMembers, error: basicError } = await supabase
        .from('team_members')
        .select('id, user_id, role, joined_at')
        .eq('team_id', currentTeam.id);

      if (basicError) {
        console.error('❌ Basic members query failed:', basicError);
        throw basicError;
      }

      console.log('👥 Raw team members data:', basicMembers);
      console.log('👥 Number of members found:', basicMembers?.length || 0);

      // Get user profiles for each member
      const members: TeamMember[] = [];
      
      console.log('👤 Loading profiles for members...');
      for (const member of basicMembers || []) {
        console.log('👤 Loading profile for user_id:', member.user_id);
        
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('first_name, last_name, email, avatar_url')
          .eq('user_id', member.user_id)
          .single();

        if (profileError) {
          console.warn('⚠️ Profile not found for user:', member.user_id, profileError);
          members.push({
            ...member,
            first_name: 'Unknown',
            last_name: 'User',
            email: `user-${member.user_id.slice(0, 8)}@unknown.com`
          });
        } else {
          console.log('✅ Profile loaded for user:', member.user_id, profile);
          members.push({
            ...member,
            first_name: profile.first_name || 'Unknown',
            last_name: profile.last_name || 'User',
            email: profile.email || `${member.user_id.slice(0, 8)}@unknown.com`,
            avatar_url: profile.avatar_url
          });
        }
      }

      console.log('👥 Final members array:', members);
      setTeamMembers(members);

      // Load team invitations (only if user is owner)
      if (currentTeam.is_owner) {
        const { data: invitations, error: invitationsError } = await supabase
          .from('team_invitations')
          .select('id, team_id, invited_by, invited_email, invitation_token, expires_at, status, role, created_at, accepted_at')
          .eq('team_id', currentTeam.id)
          .in('status', ['pending', 'sent']);

        if (invitationsError) {
          console.error('❌ Invitations query failed:', invitationsError);
          setTeamInvitations([]);
        } else {
          setTeamInvitations(invitations || []);
        }
      }

    } catch (error) {
      console.error('❌ Error loading team data:', error);
      toast.error('Failed to load team data');
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentTeam) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setIsUploadingAvatar(true);

    try {
      // Create file path: team-id/avatar.ext
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentTeam.id}/avatar.${fileExt}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('team-avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true // This overwrites existing files
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('team-avatars')
        .getPublicUrl(fileName);

      const avatarUrl = urlData.publicUrl;

      // Update team record
      const { error: updateError } = await supabase
        .from('teams')
        .update({ 
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentTeam.id);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setSettings(prev => ({ ...prev, avatar_url: avatarUrl }));
      
      // Refresh team context so the header updates
      await refreshTeams();

      toast.success('Team avatar updated successfully!');

    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
    } finally {
      setIsUploadingAvatar(false);
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!currentTeam) {
      toast.error('No team selected');
      return;
    }

    if (!user) {
      toast.error('You must be logged in to send invitations');
      return;
    }

    setIsInviting(true);

    try {
      console.log('📤 Preparing invitation for:', inviteEmail.trim());
      
      // Create unique token
      const invitationToken = crypto.randomUUID();
      
      // Create the invitation data object (but don't insert to DB yet)
      const invitationData = {
        team_id: currentTeam.id,
        invited_email: inviteEmail.trim().toLowerCase(),
        role: 'member',
        invited_by: user.id,
        invitation_token: invitationToken,
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        status: 'pending', // Use 'pending' since that's what the constraint allows
        created_at: new Date().toISOString(),
        accepted_at: null
      };

      // First check if this email is already invited to avoid duplicates
      const { data: existingInvite, error: existingError } = await supabase
        .from('team_invitations')
        .select('id')
        .eq('team_id', currentTeam.id)
        .eq('invited_email', inviteEmail.trim().toLowerCase())
        .in('status', ['pending', 'sent'])
        .maybeSingle();

      if (existingError) {
        console.error('❌ Error checking existing invitations:', existingError);
        toast.error('Failed to check existing invitations');
        return;
      }

      if (existingInvite) {
        toast.error('This email has already been invited to this team');
        return;
      }

      // Try to send email first via Edge Function
      // We'll pass the invitation data to the Edge Function to check and send
      console.log('📧 Sending email via Edge Function...');
      const { data: emailResult, error: emailError } = await supabase.functions.invoke(
        'send-team-invitation',
        {
          body: { 
            invitationData: invitationData // Pass the invitation data instead of ID
          }
        }
      );

      console.log('📧 Edge Function response:', { emailResult, emailError });

      if (emailError) {
        console.error('❌ Edge Function error:', emailError);
        toast.error('Failed to send invitation - please try again');
        return;
      }

      // Check the response from the Edge Function
      if (emailResult?.success === false) {
        // Handle specific error types
        if (emailResult.errorType === 'NOT_APPROVED') {
          toast.error('Email address is not on the approved users list.\nIf you believe this is an error, please reach out at mir@anthrogen.com');
        } else {
          toast.error(emailResult.error || 'Failed to send invitation');
        }
        return;
      }

      if (emailResult?.success) {
        console.log('✅ Email sent successfully, now creating database record...');
        
        // Only NOW create the invitation record since email was sent successfully
        const { data: invitation, error: inviteError } = await supabase
          .from('team_invitations')
          .insert(invitationData) // Use the data as-is, already has status: 'sent'
          .select()
          .single();

        if (inviteError) {
          console.error('❌ Database error after email sent:', inviteError);
          console.error('❌ Error details:', JSON.stringify(inviteError, null, 2));
          console.error('❌ Invitation data being inserted:', JSON.stringify(invitationData, null, 2));
          // Email was sent but DB failed - this is a warning, not a failure
          toast.success(`Invitation sent to ${inviteEmail}, but there was an issue saving the record. The invitation will still work.`);
        } else {
          console.log('✅ Invitation record created:', invitation.id);
          toast.success(`Invitation sent to ${inviteEmail}`);
        }
        
        setInviteEmail('');
        setShowInviteModal(false);
        loadTeamData(); // Refresh to show the new invitation
      } else {
        toast.error('Failed to send invitation');
      }
      
    } catch (error: any) {
      console.error('❌ Unexpected error inviting member:', error);
      toast.error(`Failed to send invitation: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!currentTeam) return;

    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      toast.success('Member removed successfully');
      loadTeamData();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      toast.success('Invitation cancelled');
      loadTeamData();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast.error('Failed to cancel invitation');
    }
  };

  const handleUpdateTeamSettings = async () => {
    if (!currentTeam) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('teams')
        .update({
          name: settings.name,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentTeam.id);

      if (error) throw error;

      // Refresh team context so the header updates
      await refreshTeams();

      toast.success('Team settings updated successfully!');
    } catch (error) {
      console.error('Error updating team settings:', error);
      toast.error('Failed to update team settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!currentTeam || currentTeam.is_personal) return;

    try {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', currentTeam.id);

      if (error) throw error;

      toast.success('Team deleted successfully');
      navigate('/');
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    }
  };

  const isOwner = currentTeam?.is_owner;
  const isPersonalTeam = currentTeam?.is_personal;

  // Show loading state while fetching team data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <TopBar />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <TopBar />
        <div className="pt-20 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Team Selected</h2>
            <p className="text-gray-600">Please select a team to manage settings.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isPersonalTeam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <TopBar />
        <div className="pt-20 flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Personal Team</h2>
            <p className="text-gray-600">Personal teams cannot be configured. Switch to a regular team to manage settings.</p>
          </div>
        </div>
      </div>
    );
  }

  // CHANGED: Allow both owners AND team members to access
  if (!isOwner && !isTeamMember) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <TopBar />
        <div className="pt-20 flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">Only team owners and members can access team settings.</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'members', label: 'Members', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <TopBar />
      
      <div className="pt-20">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Team Settings</h1>
            <p className="text-gray-600">
              {isOwner ? `Manage ${currentTeam.name} team settings and members` : `View ${currentTeam.name} team information`}
            </p>
          </div>

          {/* Top Navigation Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-1 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                      ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-sm border border-gray-200 p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Team Information</h2>
                  
                  {/* Team Avatar Section - Only owners can change */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Team Avatar
                    </label>
                    <div className="flex items-center gap-6">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                          {settings.avatar_url ? (
                            <img 
                              src={settings.avatar_url} 
                              alt={currentTeam.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            currentTeam.name.charAt(0).toUpperCase()
                          )}
                        </div>
                        {isUploadingAvatar && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        {/* CHANGED: Both owners AND members can change avatar */}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploadingAvatar}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                        >
                          {isUploadingAvatar ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Camera className="w-4 h-4" />
                              Change Avatar
                            </>
                          )}
                        </button>
                        <p className="text-xs text-gray-500">
                          JPG, PNG up to 5MB
                        </p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Team Name
                      </label>
                      <input
                        type="text"
                        value={settings.name}
                        onChange={(e) => setSettings(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                        placeholder="My Team"
                        readOnly={!isOwner}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Team ID
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={currentTeam.id}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 text-sm"
                          readOnly
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(currentTeam.id);
                            toast.success('Team ID copied to clipboard');
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button - Only show for owners */}
                {isOwner && (
                  <div className="flex justify-end pt-6 border-t border-gray-200">
                    <button
                      onClick={handleUpdateTeamSettings}
                      disabled={isLoading}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Settings className="h-4 w-4" />
                      {isLoading ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                )}

                {/* Danger Zone - Only show for owners */}
                {isOwner && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Danger Zone</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-red-50 rounded-sm border border-red-200">
                        <div className="flex items-center gap-3">
                          <Trash2 className="h-5 w-5 text-red-600" />
                          <div>
                            <p className="font-medium text-red-900">Delete Team</p>
                            <p className="text-sm text-red-700">Permanently delete this team and all its data</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowDeleteConfirmation(true)}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        >
                          Delete Team
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'members' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Team Members</h2>
                  {/* CHANGED: Only owners can invite members */}
                  {isOwner && (
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <UserPlus className="w-4 h-4" />
                      Invite Member
                    </button>
                  )}
                </div>

                {/* Pending Invitations - Only show for owners */}
                {isOwner && teamInvitations.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Pending Invitations</h3>
                    {teamInvitations.map((invitation) => (
                      <div key={invitation.id} className="flex items-center justify-between p-4 bg-yellow-50 rounded-sm border border-yellow-200">
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-yellow-600" />
                          <div>
                            <p className="font-medium text-yellow-900">{invitation.invited_email}</p>
                            <p className="text-sm text-yellow-700">
                              Invited as member • Status: {invitation.status} • Expires {new Date(invitation.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleCancelInvitation(invitation.id)}
                          className="px-3 py-1 text-yellow-700 hover:text-yellow-800 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Current Members */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Current Members ({teamMembers.length})</h3>
                  {teamMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-sm">
                      <div className="flex items-center gap-3">
                        {member.avatar_url ? (
                          <img 
                            src={member.avatar_url} 
                            alt={`${member.first_name} ${member.last_name}`}
                            className="w-10 h-10 rounded-full object-cover"
                            onError={(e) => {
                              // If image fails to load, hide it and show fallback
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling;
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium ${member.avatar_url ? 'hidden' : ''}`}>
                          {member.first_name?.[0] || member.email?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {member.first_name} {member.last_name}
                          </p>
                          <p className="text-sm text-gray-600">{member.email}</p>
                        </div>
                        {member.role === 'owner' && (
                          <Crown className="w-4 h-4 text-yellow-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 capitalize">{member.role}</span>
                        {/* CHANGED: Only owners can remove members (except other owners) */}
                        {isOwner && member.role !== 'owner' && (
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="px-3 py-1 text-red-600 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Invite Member Modal - Only for owners */}
          {isOwner && showInviteModal && (
            <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
              <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Invite Member</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="colleague@company.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        autoFocus
                      />
                    </div>

                    <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-sm">
                      All invited users will join as members. Only team owners can manage team settings.
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-xl">
                  <button
                    onClick={() => setShowInviteModal(false)}
                    disabled={isInviting}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInviteMember}
                    disabled={!inviteEmail.trim() || isInviting}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isInviting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      'Send Invite'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal - Only for owners */}
          {isOwner && showDeleteConfirmation && (
            <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
              <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-red-600 mb-4">Delete Team</h2>
                  
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to permanently delete <strong>{currentTeam.name}</strong>? This action cannot be undone and all team data will be lost.
                  </p>

                  <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-6">
                    <p className="text-red-800 text-sm">
                      ⚠️ <strong>Warning:</strong> This will delete all projects, members, and data associated with this team.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-xl">
                  <button
                    onClick={() => setShowDeleteConfirmation(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteTeam}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
                  >
                    Delete Team
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}